import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Stepper({
    children,
    initialStep = 1,
    onStepChange = () => { },
    onFinalStepCompleted = () => { },
    onBeforeNext = async () => true,
    stepCircleContainerClassName = '',
    stepContainerClassName = '',
    contentClassName = '',
    footerClassName = '',
    backButtonProps = {},
    nextButtonProps = {},
    backButtonText = 'Geri',
    nextButtonText = 'Devam Et',
    finalStepButtonText = 'Tamamla',
    disableStepIndicators = false,
    renderStepIndicator,
    ...rest
}) {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [direction, setDirection] = useState(0);
    const stepsArray = Children.toArray(children);
    const totalSteps = stepsArray.length;
    const isCompleted = currentStep > totalSteps;
    const isLastStep = currentStep === totalSteps;

    const updateStep = (newStep) => {
        setCurrentStep(newStep);
        if (newStep > totalSteps) {
            onFinalStepCompleted();
        } else {
            onStepChange(newStep);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setDirection(-1);
            updateStep(currentStep - 1);
        }
    };

    const handleNext = async () => {
        if (!isLastStep) {
            const canProceed = await onBeforeNext(currentStep);
            if (canProceed) {
                setDirection(1);
                updateStep(currentStep + 1);
            }
        }
    };

    const handleComplete = async () => {
        const canProceed = await onBeforeNext(currentStep);
        if (canProceed) {
            setDirection(1);
            updateStep(totalSteps + 1);
        }
    };

    return (
        <div className="flex flex-col w-full" {...rest}>
            <div className={`w-full ${stepCircleContainerClassName}`}>
                <div className={`${stepContainerClassName} flex w-full items-center justify-between mb-8`}>
                    {stepsArray.map((_, index) => {
                        const stepNumber = index + 1;
                        const isNotLastStep = index < totalSteps - 1;
                        return (
                            <React.Fragment key={stepNumber}>
                                {renderStepIndicator ? (
                                    renderStepIndicator({
                                        step: stepNumber,
                                        currentStep,
                                        onStepClick: async (clicked) => {
                                            // Prevent jumping forward without validation if needed, 
                                            // but for now let's allow clicking back.
                                            if (clicked < currentStep) {
                                                setDirection(-1);
                                                updateStep(clicked);
                                            }
                                        }
                                    })
                                ) : (
                                    <StepIndicator
                                        step={stepNumber}
                                        disableStepIndicators={disableStepIndicators}
                                        currentStep={currentStep}
                                        onClickStep={(clicked) => {
                                            if (clicked < currentStep) {
                                                setDirection(-1);
                                                updateStep(clicked);
                                            }
                                        }}
                                    />
                                )}
                                {isNotLastStep && <StepConnector isComplete={currentStep > stepNumber} />}
                            </React.Fragment>
                        );
                    })}
                </div>

                <StepContentWrapper
                    isCompleted={isCompleted}
                    currentStep={currentStep}
                    direction={direction}
                    className={`space-y-4 ${contentClassName}`}
                >
                    {stepsArray[currentStep - 1]}
                </StepContentWrapper>

                {!isCompleted && (
                    <div className={`mt-6 pb-4 ${footerClassName}`}>
                        <div className={`flex ${currentStep !== 1 ? 'justify-between' : 'justify-end'} gap-4`}>
                            {currentStep !== 1 && (
                                <button
                                    onClick={handleBack}
                                    className={`px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors ${currentStep === 1
                                        ? 'pointer-events-none opacity-50'
                                        : ''
                                        }`}
                                    {...backButtonProps}
                                    type="button"
                                >
                                    {backButtonText}
                                </button>
                            )}
                            <button
                                onClick={isLastStep ? handleComplete : handleNext}
                                className="px-6 py-2.5 rounded-xl bg-[#D4AF37] text-white font-medium hover:bg-[#B4941F] transition-colors shadow-lg shadow-[#D4AF37]/20"
                                {...nextButtonProps}
                                type="button"
                            >
                                {isLastStep ? finalStepButtonText : nextButtonText}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StepContentWrapper({ isCompleted, currentStep, direction, children, className }) {
    return (
        <motion.div
            style={{ position: 'relative', overflow: 'hidden', height: 'auto', paddingLeft: '2px', paddingRight: '2px' }}
            animate={{ height: isCompleted ? 0 : 'auto' }}
            transition={{ type: 'spring', duration: 0.4 }}
            className={className}
        >
            <AnimatePresence initial={false} mode="wait" custom={direction}>
                {!isCompleted && (
                    <SlideTransition key={currentStep} direction={direction}>
                        {children}
                    </SlideTransition>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

function SlideTransition({ children, direction }) {
    return (
        <motion.div
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="w-full"
        >
            {children}
        </motion.div>
    );
}

const stepVariants = {
    enter: (dir) => ({
        x: dir >= 0 ? 20 : -20,
        opacity: 0,
        position: 'absolute'
    }),
    center: {
        x: 0,
        opacity: 1,
        position: 'relative'
    },
    exit: (dir) => ({
        x: dir >= 0 ? -20 : 20,
        opacity: 0,
        position: 'absolute'
    })
};

export function Step({ children }) {
    return <div className="w-full">{children}</div>;
}

function StepIndicator({ step, currentStep, onClickStep, disableStepIndicators }) {
    const status = currentStep === step ? 'active' : currentStep < step ? 'inactive' : 'complete';

    const handleClick = () => {
        if (step < currentStep && !disableStepIndicators) onClickStep(step);
    };

    return (
        <motion.div
            onClick={handleClick}
            className={`relative cursor-pointer outline-none focus:outline-none z-10 ${step < currentStep ? 'cursor-pointer' : 'cursor-default'}`}
            animate={status}
            initial={false}
        >
            <motion.div
                variants={{
                    inactive: { scale: 1, backgroundColor: '#FAF9F6', color: '#A3A3A3', borderColor: '#E5E7EB' },
                    active: { scale: 1.1, backgroundColor: '#D4AF37', color: '#FFFFFF', borderColor: '#D4AF37' },
                    complete: { scale: 1, backgroundColor: '#D4AF37', color: '#FFFFFF', borderColor: '#D4AF37' }
                }}
                transition={{ duration: 0.3 }}
                className="flex h-8 w-8 items-center justify-center rounded-full font-bold border-2 text-sm shadow-sm"
            >
                {status === 'complete' ? (
                    <CheckIcon className="h-4 w-4" />
                ) : (
                    <span>{step}</span>
                )}
            </motion.div>
        </motion.div>
    );
}

function StepConnector({ isComplete }) {
    const lineVariants = {
        incomplete: { width: 0, backgroundColor: 'transparent' },
        complete: { width: '100%', backgroundColor: '#D4AF37' }
    };

    return (
        <div className="relative mx-2 h-[2px] flex-1 overflow-hidden rounded bg-gray-100">
            <motion.div
                className="absolute left-0 top-0 h-full"
                variants={lineVariants}
                initial={false}
                animate={isComplete ? 'complete' : 'incomplete'}
                transition={{ duration: 0.4 }}
            />
        </div>
    );
}

function CheckIcon(props) {
    return (
        <svg {...props} fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
            <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.1, type: 'tween', ease: 'easeOut', duration: 0.3 }}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
            />
        </svg>
    );
}
